/**
 * AP-11 — Vector-ASC-Format, portiert aus `src/trace/TraceWriter.cpp` und
 * `src/trace/TraceReader.cpp`.
 *
 * Zeilenform: `<ts> 1 <id>[x] <Rx|Tx> d <dlc> <byte>...`
 *
 * Zwei bewusste Korrekturen gegenüber dem Original (Begründung in PROGRESS.md):
 * 1. Das `x`-Suffix markiert **nur noch** 29-Bit-IDs. `TraceWriter.cpp` hängt es
 *    per `"%03Xx"`/`"%08Xx"` an *jede* ID an, `TraceReader.cpp` liest genau
 *    dieses `x` aber als „extended" — jeder 11-Bit-Frame kam damit als 29-Bit
 *    zurück. `parseAscLine()` liest Altdateien trotzdem richtig, siehe unten.
 * 2. Zeitstempel mit 6 Nachkommastellen statt `operator<<`-Standard (6
 *    *signifikante* Stellen) — dort schrumpfte die Auflösung mit wachsender
 *    Laufzeit still weg: `1234.5678` wurde zu `1234.57`, ab ~2,8 h Laufzeit
 *    blieb nur noch eine Nachkommastelle.
 */

import type { CanFrame } from '../can/ICanAdapter';
import {
  clampDlc,
  formatDataBytes,
  parseDataBytes,
  parseHex,
  type TraceWriter,
} from './types';

const ASC_HEADER = 'date CAN_MON_Cpp_FULL trace\nbase hex  timestamps absolute\nno internal events logged\n';

export function createAscWriter(): TraceWriter {
  return {
    header: () => ASC_HEADER,
    frame(frame: CanFrame, isRx: boolean): string {
      const id = frame.extended
        ? `${frame.id.toString(16).toUpperCase().padStart(8, '0')}x`
        : frame.id.toString(16).toUpperCase().padStart(3, '0');
      return `${frame.timestamp_s.toFixed(6)} 1 ${id} ${isRx ? 'Rx' : 'Tx'} d ${frame.dlc}${formatDataBytes(frame)}\n`;
    },
  };
}

export function parseAscLine(line: string): CanFrame | null {
  // Kopf-/Kommentarzeilen beginnen nie mit einer Ziffer.
  const first = line.charCodeAt(0);
  if (!(first >= 0x30 && first <= 0x39)) return null;

  const tokens = line.trim().split(/\s+/);
  const [tsToken, channelToken, idToken, , dToken, dlcToken] = tokens;
  if (tsToken === undefined || idToken === undefined || dlcToken === undefined) return null;
  if (dToken !== 'd') return null;

  const timestamp = Number(tsToken);
  if (!Number.isFinite(timestamp)) return null;
  if (channelToken === undefined || !Number.isFinite(Number(channelToken))) return null;

  const dlcValue = Number(dlcToken);
  if (!Number.isFinite(dlcValue)) return null;

  const hasSuffix = idToken.endsWith('x') || idToken.endsWith('X');
  const idHex = hasSuffix ? idToken.slice(0, -1) : idToken;
  const id = parseHex(idHex);
  if (id === null) return null;

  // Altdateien hängen das `x` auch an 11-Bit-IDs; die schrieb das Original
  // aber mit `%03X`, extended dagegen mit `%08X`. Über die Hex-Länge lassen
  // sich beide Fälle unterscheiden, ohne neue Dateien falsch zu lesen.
  const extended = hasSuffix && idHex.length > 3;

  const dlc = clampDlc(dlcValue);
  return {
    id,
    dlc,
    data: parseDataBytes(tokens.slice(6), dlc),
    extended,
    remote: false,
    timestamp_s: timestamp,
  };
}
